import { Separator } from '@/components/ui/separator'
import { PROJECTED_NOTE } from './compare-table'
import { OTHER_NOTE } from './group-table'
import { pageInfo } from './scope-list'

// 术语，按 agent-roas knowledge/global/glossary.md
const GLOSSARY = [
  ['队列口径', '按安装日队列算每批买入用户的回收，回答「新买的用户以后能赚多少」。本页所有数字都是这个口径：每花 1 块钱，未来 N 天回本多少。'],
  ['ROAS', '只指队列口径：花 1 块钱在未来 N 天累计收回多少。'],
  ['预测 D90 / D180', '近 3 个月队列的 D30 实测 × 同批成熟队列的 D90/D30、D180/D30 倍数，按花费加权；是预测值，不是已实现值。'],
  ['回本线', 'D90 ≥ 100%，资金安全线；回本日为预测曲线首次 ≥ 100% 的天龄。'],
  ['目标线', '历史可达线：近 12 个月满 180 天的安装月中 D180 前三名成本加权合成，按 pid 各一条，给 D30 / D60 / D90 / D180 四个检查点；本页按各格日花费加权合成。'],
  ['D180 贡献毛利', '每日买入用户 180 天内的预测贡献毛利 = Σ 日花费 × (预测 D180 − 1)。'],
  ['终值', '180 天以后的回收；LTV = D180 × 终值倍数（保守 = D365/D180），只作远期参照。'],
  ['样本覆盖', 'LTV 卡的样本覆盖 = 有终值倍数的格子占花费的比例。'],
]

const TONE_RULES = [
  ['预测 D90 标色', '预测 D90 < 97% 标红，97%～103%（回本线 ±3 点预测误差带）标黄；KPI、业务群表、五窗口表、产品对比同一规则。'],
  ['较主角标色', '同期对比 D180 悬停看较主角 = 主角 D180 − 该行 D180：正数绿 = 主角更好，负数红，±1 点内不标色。'],
  ['小花费行', '同期对比中日花费 < $100 的行置灰，只作参考。'],
  ['其他国家格子', OTHER_NOTE],
]

const TABLE_RULES = [
  ['业务群 / 国家对比表', '按产品树下一层分行（根与有子分组的产品线按子分组，pid 按国家）；分组行展示贡献毛利；合计行即当前范围，与 KPI 一致；点行把范围收窄到该行。'],
  [
    '按最近 N 天队列的回收预测（五窗口）',
    '窗口 = 安装日在（截止日 − N，截止日]的队列，只含进合计的格子。每个安装日队列按当前天龄 a 的实测累计收入 × 该格成熟队列 D180/Da 倍数外推（本格无样本退 pid 级），a < 7（neku 为 a < 8）不外推、花费计为未入模；LTV = 预测 D180 × 保守终值倍数。上标 预 表示含外推或预测，? 表示状态未知，未标记为实测或根据实测计算。越短的窗口外推成分越大，只作趋势参照。悬停单元格看队列数、平均天龄、未入模占比、D90、LTV 乐观值与倍数来源。',
  ],
  [
    '产品对比',
    '花费占比为占当前范围日均花费。可达线 D180 与历史最好月（月份 · 当月 D180 日贡献毛利）按 pid 取自数据盘该单元；距可达线 = 可达线 D180 − 预测 D180。合计行即当前范围，与 KPI 一致。',
  ],
  [
    '同期对比 · 对照日期',
    '主角 = 选定安装日的队列（默认最新安装日）；对照：昨天、上周同日（− 7 天）、上月同日（上个自然月同一日，无该日取月末）、去年同日（去年同月同日，2 月 29 日取 2 月 28 日）；均线：上周、去年同周（去年同日所在周）按自然周（周一起），本月（至主角前一天）、上月、去年同月按自然月，按天龄逐点 Σ 收入 ÷ Σ 花费。',
  ],
  [
    '同期对比 · 口径',
    `同天龄累计 ROAS，不是当日收入 ÷ 当日花费。格子集合与五窗口表一致（入模格子，小国已合并为「其他国家」格子，排除格子与观察格不计）。各队列已满的天龄取实测，之后按成熟队列倍数逐格外推（天龄 < 7、neku < 8 不外推）；${PROJECTED_NOTE}；D0 永远是实测；回本日 = 累计 ROAS 首次 ≥ 100% 的天龄。CPI 只展示不作判据。`,
  ],
]

function Section({ title, items }) {
  return (
    <section className="flex flex-col gap-3">
      {title && <h3 className="text-base font-semibold">{title}</h3>}
      <dl className="grid gap-3">
        {/* 固定术语与接口公式可能同名（如目标线），key 带序号 */}
        {items.map(([term, text], index) => (
          <div key={`${index}-${term}`}>
            <dt className="font-medium">{term}</dt>
            <dd className="mt-1 text-muted-foreground">{text ?? '-'}</dd>
          </div>
        ))}
      </dl>
    </section>
  )
}

// Archived snapshots can still contain formula text from before the fixed-cost card was removed.
function formulaText({ name, expression }) {
  if (name === 'D180 贡献毛利 / 天' && expression?.includes('固定开支')) {
    return 'Σ 格子日花费 × (预测 D180 − 1)，逐格取数据盘 profit180 求和'
  }
  if (name === '下钻行') return expression?.replace('，不分摊固定开支', '')
  return expression
}

/**
 * 「术语与公式」抽屉（MON-68）。MON-89 正文说明统一收进这里：
 * 术语与公式（无小标题，MON-91）→ 标色与噪声规则 → 各表口径 → 本页信息（data 未到时不显示）
 */
export function Glossary({ formulas = [], data }) {
  const formulaItems = formulas.map((formula) => [formula.name, formulaText(formula)])
  return (
    <div className="flex flex-col gap-6 text-sm">
      <Section items={[...GLOSSARY, ...formulaItems]} />
      <Separator />
      <Section title="标色与噪声规则" items={TONE_RULES} />
      <Separator />
      <Section title="各表口径" items={TABLE_RULES} />
      {data && (
        <>
          <Separator />
          <Section title="本页信息" items={pageInfo(data)} />
        </>
      )}
    </div>
  )
}
